import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import * as Act from './reports.actions';
import {endpoints} from '../../constants/endpoints';
import {IReport} from '../../interfaces/report.interface';
import {ITransaction} from '../../interfaces/transaction.interface';
import {IDeaRecord, IDeaReportRecord} from '../../interfaces/dea-record.interface';
import {ReportTypeEnum} from '../../interfaces/ReportTypeEnum';
import {AuthState} from '../auth/auth.state';
import {IInventoryTotals} from '../../interfaces/IInventoryTotals';
import { TransactionsService } from '../../services/transactions.service';
import { ITransactionModel } from '../../interfaces/ITransactionModel';
import { PharmacyState } from '../pharmacy/pharmacy.state';
import { deepCopy } from '../../helpers/helpers';
import { DrugScheduleEnum } from '../../interfaces/DrugScheduleEnum ';
import { INdcPurchaseRecord } from '../../interfaces/INdcPurchaseRecord.interface';
import { ICurrentInventoryReportNdcModel } from 'src/app/reports/interfaces/current-inventory-report-ndc-model.interface';
import { ISohHistoryReportItem } from 'src/app/reports/components/soh-history-report/interfaces/soh-history-report-item.interface';

export interface ReportsStateModel {
  loading: boolean;
  success: boolean;
  newDeaReportId: string;
  reportRecords: ITransactionModel[];
  currentReport: IReport;
  deaRecords: IDeaRecord[];
  deaReportRecords: IDeaReportRecord[];
  newReportData: any;
  customReportForm: any;
  currentInventoryReportForm: any;
  inventoryTotalsForm: any;
  ndcPurchaseReportControlsState: any;
  similarOnHandHistoryReportControlsState: any;
  runningReports: boolean;
  inventoryTotals: IInventoryTotals[];
  inventoryTotalsFrom: string;
  inventoryTotalsTo: string;
  ndcPurchaseRecords: INdcPurchaseRecord[];
  currentInventoryRecords: ICurrentInventoryReportNdcModel[];
  similarOnHandHistoryReportData: ISohHistoryReportItem[];
}

@State<ReportsStateModel>({
  name: 'reports',
})

@Injectable()
export class ReportsState {

  constructor(
    private http: HttpClient,
    private store: Store,
    private transactionService: TransactionsService
  ) {
  }

  @Selector()
  static loading(state: ReportsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: ReportsStateModel): boolean {
    return state.success;
  }

  @Selector()
  static deaRecords(state: ReportsStateModel): IDeaRecord[] {
    return state.deaRecords;
  }

  @Selector()
  static deaReportRecords(state: ReportsStateModel): IDeaReportRecord[] {
    return state.deaReportRecords;
  }

  @Selector()
  static newDeaReportId(state: ReportsStateModel): string {
    return state.newDeaReportId;
  }

  @Selector()
  static reportRecords(state: ReportsStateModel): ITransactionModel[] {
    return state.reportRecords;
  }

  @Selector()
  static currentReport(state: ReportsStateModel): any {
    return state.currentReport;
  }

  @Selector()
  static newReportData(state: ReportsStateModel): any {
    return state.newReportData;
  }

  @Selector()
  static customReportForm(state: ReportsStateModel): any {
    return state.customReportForm;
  }

  @Selector()
  static currentInventoryReportForm(state: ReportsStateModel): any {
    return state.currentInventoryReportForm;
  }


  @Selector()
  static inventoryTotalsForm(state: ReportsStateModel): any {
    return state.inventoryTotalsForm;
  }


  @Selector()
  static runningReports(state: ReportsStateModel): any {
    return state.runningReports;
  }

  @Selector()
  static inventoryTotals(state: ReportsStateModel): IInventoryTotals[] {
    return state.inventoryTotals;
  }

  @Selector()
  static ndcPurchaseRecords(state: ReportsStateModel): INdcPurchaseRecord[] {
    return state.ndcPurchaseRecords;
  }

  @Selector()
  static ndcPurchaseReportControlsState(state: ReportsStateModel): any {
    return state.ndcPurchaseReportControlsState;
  }

  @Selector()
  static currentInventoryRecords(state: ReportsStateModel): ICurrentInventoryReportNdcModel[] {
    return state.currentInventoryRecords;
  }
  
  @Selector()
  static similarOnHandHistoryReportData(state: ReportsStateModel): ISohHistoryReportItem[] {
    return state.similarOnHandHistoryReportData;
  }

  @Selector()
  static similarOnHandHistoryReportControlsState(state: ReportsStateModel): ISohHistoryReportItem[] {
    return state.similarOnHandHistoryReportControlsState;
  }



  @Action(Act.GetDeaRecords)
  getDeaRecords(ctx: StateContext<ReportsStateModel>, {}: Act.GetDeaRecords): Observable<any> {
    ctx.patchState({loading: true, deaRecords: []});

    return this.http.get(`${endpoints.report.getReportRecords}/${ReportTypeEnum.DeaReport}`)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            deaRecords: resp.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetDeaReportRecords)
  getDeaReportRecords(ctx: StateContext<ReportsStateModel>, {}: Act.GetDeaReportRecords): Observable<any> {
    ctx.patchState({loading: true, deaReportRecords: []});

    return this.http.get(`${endpoints.report.getReportRecords}/${ReportTypeEnum.DeaReport}`)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            deaReportRecords: resp.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SignReport)
  signReport(ctx: StateContext<ReportsStateModel>, {reportId, base64SignerSignature, signerPin, base64WitnessSignature, witnessPin, ascending, includeLocations}: Act.SignReport): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.post(endpoints.report.dea.sign, {reportId, base64SignerSignature, signerPin, base64WitnessSignature, witnessPin, sortAscending: ascending, withLocations: includeLocations})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            currentReport: resp.value
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreateNewDeaReport)
  createNewDeaReport(ctx: StateContext<ReportsStateModel>, {timeType, isBiennial, title, isAllDrugsMode, schedulesToInclude, locations}: Act.CreateNewDeaReport): Observable<any> {
    ctx.patchState({loading: true, newDeaReportId: undefined, currentReport: undefined});
    const userId = this.store.selectSnapshot(AuthState.activeUserId);
    schedulesToInclude = !schedulesToInclude.length ? [DrugScheduleEnum.C2, DrugScheduleEnum.C3, DrugScheduleEnum.C4, DrugScheduleEnum.C5, DrugScheduleEnum.MISC] : schedulesToInclude;
    const model = {timeType, title, isBiennial, userId, schedulesToInclude, isAllDrugsMode, locations};
    return this.http.post(endpoints.report.dea.runReport, model)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            newDeaReportId: responseData?.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetCustomReportRecords)
  getCustomReportRecords(ctx: StateContext<ReportsStateModel>, {filters}: Act.GetCustomReportRecords): Observable<any> {
    ctx.patchState({loading: true, reportRecords: []});

    // TODO: replace with real endpoint
    // return this.http.get(endpoints.report.getReportRecords + filters)
    return of({})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            reportRecords: resp?.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetCurrentInventoryReportRecords)
  setCurrentInventoryReportRecords(ctx: StateContext<ReportsStateModel>, {records}: Act.SetCurrentInventoryReportRecords): void {
    ctx.patchState({currentInventoryRecords: records || []});
  }

  @Action(Act.SetCustomReportRecords)
  setCustomReportRecords(ctx: StateContext<ReportsStateModel>, {records}: Act.SetCustomReportRecords): void {
    ctx.patchState({reportRecords: records || []});
  }

  @Action(Act.SetSimilarOnHandHistoryRecords)
  setSimilarOnHandHistoryReport(ctx: StateContext<ReportsStateModel>, {records}: Act.SetSimilarOnHandHistoryRecords): void {
    ctx.patchState({similarOnHandHistoryReportData: records || []});
  }

  @Action(Act.SetInventoryTotals)
  setInventoryTotals(ctx: StateContext<ReportsStateModel>, {records}: Act.SetInventoryTotals): void {
    ctx.patchState({inventoryTotals: records || []});
  }

  @Action(Act.SetNdcPurchaseRecords)
  setNdcPurchaseRecords(ctx: StateContext<ReportsStateModel>, {records}: Act.SetNdcPurchaseRecords): void {
    ctx.patchState({ndcPurchaseRecords: records || []});
  }

  @Action(Act.SetDeaRecordsList)
  setDeaRecordsList(ctx: StateContext<ReportsStateModel>, {records}: Act.SetDeaRecordsList): void {
    ctx.patchState({deaRecords: records || []});
  }

  @Action(Act.SaveNewReportData)
  saveNewReportData(ctx: StateContext<ReportsStateModel>, {data}: Act.SaveNewReportData): void {
    ctx.patchState({newReportData: data || null});
  }

  @Action(Act.GetReportById)
  getReportById(ctx: StateContext<ReportsStateModel>, {reportId}: Act.GetReportById): Observable<any> {
    ctx.patchState({loading: true, currentReport: {} as IReport});

    return this.http.get(`${endpoints.report.dea.getReport}/${reportId}`)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            currentReport: resp.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetDeaReportDetailsList)
  setDeaReportDetailsList(ctx: StateContext<ReportsStateModel>, {records}: Act.SetDeaReportDetailsList): void {
    const reportCopy = Object.assign({}, ctx.getState().currentReport) as IDeaRecord;
    reportCopy.details = records || [];
    ctx.patchState({currentReport: reportCopy});
  }

  @Action(Act.SaveDeaReportDetailsRecord)
  saveDeaReportDetailsRecord(ctx: StateContext<ReportsStateModel>, {index, record}: Act.SaveDeaReportDetailsRecord): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.report.dea.saveDetail, record)
      .pipe(
        tap((resp: any) => {
          // update only the changed row so we don't have to refresh the entire model
          if (resp.success) {
            const reportCopy = deepCopy(ctx.getState().currentReport) as IDeaRecord;
            reportCopy.details[index] = resp.value;
            ctx.patchState({currentReport: reportCopy})
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RunInventoryTotalsReport)
  runInventoryTotalsReport(ctx: StateContext<ReportsStateModel>, {payload}: Act.RunInventoryTotalsReport): Observable<any> {
    ctx.patchState({loading: true, runningReports: true});
    return this.http.post(endpoints.report.inventoryTotals.runReport, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            runningReports: false,
            inventoryTotals: resp?.value?.totals,
            inventoryTotalsFrom: resp?.value?.startDate,
            inventoryTotalsTo: resp?.value?.endDate,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RunCustomReport)
  runCustomReport(ctx: StateContext<ReportsStateModel>, {reportData}: Act.RunCustomReport): Observable<any> {
    ctx.patchState({loading: true, runningReports: true});
    return this.http.post(endpoints.report.custom.runReport, reportData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: !!resp?.value?.length,
            runningReports: false,
            reportRecords: this.transactionService.mapTransactions(resp.value || []),
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RunSimilarOnHandHistoryReport)
  runSimilarOnHandHistoryReport(ctx: StateContext<ReportsStateModel>, {reportData}: Act.RunSimilarOnHandHistoryReport): Observable<any> {
    ctx.patchState({loading: true, runningReports: true});
    return this.http.post(endpoints.report.soh.runReport, reportData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: !!resp?.value?.length,
            runningReports: false,
            similarOnHandHistoryReportData: this.transactionService.mapTransactions(resp.value || []),
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RunNdcPurchaseReport)
  runNdcPurchaseReport(ctx: StateContext<ReportsStateModel>, {reportData}: Act.RunNdcPurchaseReport): Observable<any> {
    ctx.patchState({loading: true, runningReports: true});
    return this.http.post(endpoints.report.ndcPurchase.runReport, reportData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: !!resp?.value?.length,
            runningReports: false,
            ndcPurchaseRecords: resp.value || [],
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveCustomReportFormState)
  saveCustomReportFormState(ctx: StateContext<ReportsStateModel>, {formData}: Act.SaveCustomReportFormState): void {
    ctx.patchState({customReportForm: formData || null});
  }

  @Action(Act.SaveCurrentInventoryReportFormState)
  saveCurrentInventoryReportFormState(ctx: StateContext<ReportsStateModel>, {formData}: Act.SaveCurrentInventoryReportFormState): void {
    ctx.patchState({currentInventoryReportForm: formData || null});
  }

  @Action(Act.SaveInventoryTotalsFormState)
  saveInventoryTotalsFormState(ctx: StateContext<ReportsStateModel>, {formData}: Act.SaveInventoryTotalsFormState): void {
    ctx.patchState({inventoryTotalsForm: formData || null});
  }

  @Action(Act.SaveNdcPurchaseReportControlsState)
  saveNdcPurchaseReportControlsState(ctx: StateContext<ReportsStateModel>, {formData}: Act.SaveNdcPurchaseReportControlsState): void {
    ctx.patchState({ndcPurchaseReportControlsState: formData || null});
  }

  @Action(Act.SaveSimilarOnHandHistoryReportControlsState)
  saveSimilarOnHandHistoryReportControlsState(ctx: StateContext<ReportsStateModel>, {formData}: Act.SaveSimilarOnHandHistoryReportControlsState): void {
    ctx.patchState({similarOnHandHistoryReportControlsState: formData || null});
  }

  @Action(Act.SetLoading)
  setLoading(ctx: StateContext<ReportsStateModel>, {isLoading}: Act.SetLoading): void {
    ctx.patchState({loading: isLoading});
  }

  @Action(Act.RunCurrentInventoryReport)
  runCurrentInventoryReport(ctx: StateContext<ReportsStateModel>, {request}: Act.RunCurrentInventoryReport): Observable<ReportsStateModel> {
    ctx.patchState({loading: true, runningReports: true});

    let params = new HttpParams();

    if (request?.drugIds?.length) {
      request.drugIds.forEach(id => params = params.append('drugIds', id));
    }
    if (request?.schedules?.length) {
      request.schedules.forEach(id => params = params.append('schedules', id));
    }
    if (request?.locationIds?.length) {
      request.locationIds.forEach(id => params = params.append('locationIds', id));
    }

    return this.http.get(endpoints.report.currentInventory.runReport, {params})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            runningReports: false,
            currentInventoryRecords: resp.value || [],
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, runningReports: false});
          return of(null);
        }),
      );
  }
}
